/* eslint-disable react-hooks/exhaustive-deps */

import { useTranslate } from "@/hooks/use-translate/useTranslate";
import type { IQuestion } from "@/types/types";
import type { InpectionPhase } from "../../inspection.d";
import { useQuery } from "@tanstack/react-query";
import { usePhase } from "@/store/usePhase";
import { getAnswers, getAnswersByPhases } from "@/lib/services/answers";
import { useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import QuestionCard from "./QuestionCard";
import Button from "@/components/buttons/button/Button";
import { closeNaPhase } from "@/lib/services/answers";
import { getInspectionId } from "@/lib/services/inspections";
import useInspectionId from "@/store/useInspectionId";
import ProblemMessage from "@/components/problem-message/ProblemMessage";
import CloseInspectionForm from "../CloseInspectionForm";
import { useUserStore } from "@/store/useUser";
import CloseNaPhaseModal from "../CloseNaPhaseModal";

const InspectionViewMobile = ({
  questions,
  phases,
}: {
  questions: IQuestion[];
  phases: InpectionPhase[];
}) => {
  const { inspectionId } = useParams() as { inspectionId: string };
  const translate = useTranslate();
  const { updateInspectionData } = useInspectionId();

  const { setCompleted, setCurrentPhase, setAllNa } = usePhase();
  const currentPhase = usePhase((store) => store.currentPhase!);
  const phaseLabel = usePhase((store) => store.phaseLabel!);
  const { isSupervisor } = useUserStore();

  const queryKey = useMemo(() => `answers-${currentPhase}`, [currentPhase]);

  const { data: currentData } = useQuery({
    queryKey: [queryKey, { inspectionId, currentPhase }],
    queryFn: () =>
      getAnswers({
        inspectionId: Number(inspectionId),
        phaseId: currentPhase,
      }),
    placeholderData: [],
  });

  const { data: restOfAnswers } = useQuery({
    queryKey: ["not_current_phases", { inspectionId, currentPhase }],
    queryFn: () =>
      getAnswersByPhases({
        inspectionId: Number(inspectionId),
        phasesId: phases
          .filter((phase) => phase.phaseId !== currentPhase)
          .map((phase) => phase.phaseId),
      }),

    placeholderData: {},
  });

  const [noNaAnswers, setNoNaAnswers] = useState<number[]>([]);
  const [errorNa, setErrorNa] = useState(false);
  const [closeNaPhaseModal, setCloseNaPhaseModal] = useState(false);
  const [closeInspectionModal, setCloseInspectionModal] = useState(false);

  useEffect(() => {
    if (currentPhase && inspectionId && currentData) {
      setCompleted(currentData.length);
      const allNa = currentData.every(
        (answer: { status: string }) =>
          answer.status && answer.status === "N/A",
      );
      setAllNa(allNa);
    }
  }, [currentData, currentPhase]);

  const closeWhitAllNa = async (esnOut?: string) => {
    if (noNaAnswers.length > 0 || isSupervisor) return;
    try {
      await closeNaPhase(
        inspectionId,
        currentPhase,
        typeof esnOut === "string" ? esnOut : undefined,
      );
      const newInspectionData = await getInspectionId({
        id: Number(inspectionId),
      });
      updateInspectionData(newInspectionData);
      setCurrentPhase(newInspectionData.phase);
      setCloseInspectionModal(false);
      window.location.reload();
    } catch {
      setCloseInspectionModal(false);
      setErrorNa(true);
    }
  };

  const closeInspectionWithNaLastPhase = async (
    phaseId: number,
    esnOut: string,
  ) => {
    if (
      !esnOut.length ||
      phaseId !== 4 ||
      noNaAnswers.length > 0 ||
      isSupervisor
    )
      return;
    closeWhitAllNa(esnOut);
  };

  return (
    <div className="">
      {!isSupervisor &&
      (phaseLabel === "disassemblyInspection" || phaseLabel === "mdpRepair") ? (
        <div className="pt-4 pl-4">
          <Button
            onClick={closeWhitAllNa}
            label={translate("naPhase")}
            type="primary-red"
            size="small"
            disabled={noNaAnswers.length > 0}
            width="100px"
          />
        </div>
      ) : null}
      {!isSupervisor && phaseLabel === "ccInspection" ? (
        <div className="pt-4 pl-4">
          <Button
            onClick={() => setCloseInspectionModal(true)}
            label={translate("naPhase")}
            type="primary-red"
            size="small"
            disabled={noNaAnswers.length > 0}
            width="100px"
          />{" "}
        </div>
      ) : null}

      <div className="p-4">
        <h3 className="text-ib-main font-ib-bold text-ib-gray-darksest">
          {translate("actionToDo")}
        </h3>
      </div>
      <div className="grid grid-cols-1 divide-y divide-ib-gray-light-1">
        {questions.map((question, index) => {
          let answer;

          if (restOfAnswers?.phases && restOfAnswers.phases[currentPhase]) {
            answer = restOfAnswers.phases[currentPhase]?.find(
              (answer: any) => answer.questionId === question.questionId,
            );
          } else {
            answer = currentData?.find(
              (answer: any) => answer.questionId === question.questionId,
            );
          }
          if (
            answer?.status &&
            answer.status !== "N/A" &&
            !noNaAnswers.includes(answer.id)
          )
            setNoNaAnswers((prev) => [...prev, answer.id]);
          if (
            answer?.status &&
            answer.status === "N/A" &&
            noNaAnswers.includes(answer.id)
          )
            setNoNaAnswers((prev) => prev.filter((id) => id !== answer.id));

          return (
            <QuestionCard
              key={`mobile-question-${question.questionId}`}
              question={question}
              answer={answer}
              index={index + 1}
              restOfAnswers={restOfAnswers?.phases || {}}
              phaseDate={{
                phaseLabel,
                currentPhase,
                phases,
              }}
            />
          );
        })}
      </div>
      {errorNa ? (
        <ProblemMessage
          error={translate("noPosibleSaveChange")}
          closeMessage={() => setErrorNa(false)}
        />
      ) : null}
      {closeInspectionModal ? (
        <CloseInspectionForm
          closeInspection={closeInspectionWithNaLastPhase}
          cancel={() => setCloseInspectionModal(false)}
          isNaClose
        />
      ) : null}
      {closeNaPhaseModal ? (
        <CloseNaPhaseModal
          closeWhitAllNa={closeWhitAllNa}
          cancel={() => setCloseNaPhaseModal(false)}
        />
      ) : null}
    </div>
  );
};
export default InspectionViewMobile;
